import { faCircleQuestion as fasCircleQuestion } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Console } from "@utils/console";
import autobind from "autobind-decorator";
import type { FieldState } from "final-form";
import compact from "lodash/compact";
import React from "react";
import type { ReactNode } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import { Field } from "react-final-form";
import type { FieldRenderProps } from "react-final-form";

export const VALIDATION_TOOLTIP = true;

export interface FormItemProps<FieldType = string> {
	className?: string;
	name: string;
	label?: ReactNode;
	info?: ReactNode;
	infoTooltip?: ReactNode;
	tabIndex?: number;

	disabled?: boolean;
	readOnly?: boolean;
	required?: boolean;

	// Deprecated
	inline?: boolean;
	autoReset?: boolean;
	infoFirst?: boolean;

	validate?: (value: FieldType, allValues: Record<string, any>, meta?: FieldState<FieldType>) => string;

	onChange?: (value: FieldType) => void;
	onBlur?: (value: FieldType, event: React.FocusEvent<HTMLElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
}

export type FormItemState = {};

export abstract class FormItem<
	FieldType = string,
	Props extends FormItemProps<FieldType> = FormItemProps<FieldType>,
	State extends FormItemState = FormItemState,
> extends React.Component<Props, State> {
	protected static idCounter = 0;
	protected readonly id: string;
	protected readonly defaultValue: FieldType = undefined;
	protected handleProtectedChange: (value: FieldType) => void;
	protected lastValue: FieldType;

	public constructor(props: Props) {
		super(props);
		this.id = `${FormItem.idCounter}`;
		FormItem.idCounter += 1;

		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		return {} as unknown as State;
	}

	public componentDidMount(): void {}

	public componentWillUnmount(): void {
		const { name } = this.props;

		try {
			if (this.handleProtectedChange && !name.includes(".")) {
				this.handleProtectedChange(undefined);
			}
		} catch (reason) {
			Console.error(reason);
		}
	}

	protected shouldFieldRevalidate(prevProps: Props, _prevState: State): boolean {
		const { required, disabled, readOnly } = this.props;

		return (
			prevProps.required !== (required as boolean) ||
			prevProps.disabled !== (disabled as boolean) ||
			prevProps.readOnly !== (readOnly as boolean)
		);
	}

	public componentDidUpdate(prevProps: Props, prevState: State): void {
		if (this.shouldFieldRevalidate(prevProps, prevState)) {
			if (this.handleProtectedChange) {
				const value = this.lastValue;
				this.handleProtectedChange(undefined);
				this.handleProtectedChange(value);
			}
		}
	}

	protected isDisabled(): boolean {
		const { disabled, readOnly } = this.props;

		return disabled || readOnly;
	}

	public render(): ReactNode {
		const { name } = this.props;

		return (
			<Field<FieldType>
				name={name}
				validate={this.handleValidate}
				parse={this.parse}
				format={this.format}
				defaultValue={this.defaultValue}
			>
				{(fieldProps: FieldRenderProps<FieldType>): ReactNode => {
					const { autoReset, disabled } = this.props;
					const {
						input: { onChange, value, onBlur },
					} = fieldProps;

					this.handleProtectedChange = onChange; // TODO: Ez nem a legszebb dolog
					this.lastValue = value ? JSON.parse(JSON.stringify(value)) : value;

					if (autoReset && disabled && value !== undefined) {
						setTimeout(() => {
							this.handleChange(undefined, fieldProps);
							onBlur(undefined);
						});
					}

					if (VALIDATION_TOOLTIP) {
						const {
							meta: { error },
						} = fieldProps;
						const show = this.isValidationShow(fieldProps);
						const defaultShow = this.isValidationDefaultShow(fieldProps);

						return (
							<OverlayTrigger
								show={show}
								defaultShow={defaultShow}
								data-testid={`${name}-overlay`}
								flip
								overlay={(props: OverlayInjectedProps) =>
									this.renderToolTip(`errorTooltip-${name}`, props, error, true)
								}
							>
								<div className="w-100" data-testid={`${name}`}>
									{this.renderInnerElement(fieldProps)}
								</div>
							</OverlayTrigger>
						);
					}
				}}
			</Field>
		);
	}

	private renderToolTip(
		id: string,
		props: OverlayInjectedProps,
		tooltip: ReactNode,
		isErrorToolTip: boolean,
	): ReactNode {
		const toolTipVariant = isErrorToolTip ? "danger" : "black";
		return (
			<Tooltip id={id} className={`tooltip-${toolTipVariant}`} {...props}>
				{tooltip}
			</Tooltip>
		);
	}

	private renderInnerElement(fieldProps: FieldRenderProps<FieldType>): ReactNode {
		const { inline } = this.props;

		if (inline) {
			return this.renderInline(fieldProps);
		}

		return this.renderCol(fieldProps);
	}

	protected renderInline(fieldProps: FieldRenderProps<FieldType>): ReactNode {
		const { info } = this.props;

		return (
			<Form.Group as={Row} className={this.getGroupClassName(fieldProps)}>
				<Col sm={4}>{this.renderLabel(fieldProps)}</Col>
				<Col sm={8}>
					{this.renderField(fieldProps)}
					{this.renderInfo(info)}
					{!VALIDATION_TOOLTIP && this.renderValidation(fieldProps)}
				</Col>
			</Form.Group>
		);
	}

	protected renderCol(fieldProps: FieldRenderProps<FieldType>): ReactNode {
		const { info, infoFirst } = this.props;

		return (
			<Form.Group className={this.getGroupClassName(fieldProps)}>
				{this.renderLabel(fieldProps)}
				{infoFirst && this.renderInfo(info)}
				{this.renderField(fieldProps)}
				{!infoFirst && this.renderInfo(info)}
				{!VALIDATION_TOOLTIP && this.renderValidation(fieldProps)}
			</Form.Group>
		);
	}

	protected renderLabel(fieldProps: FieldRenderProps<FieldType>): ReactNode {
		const { label, infoTooltip, disabled, readOnly, name } = this.props;
		const {
			meta: { invalid, touched },
		} = fieldProps;

		if (!label) {
			return null;
		}

		if (infoTooltip) {
			return (
				<OverlayTrigger
					overlay={(props: OverlayInjectedProps) =>
						this.renderToolTip(`infoTooltip-${name}`, props, infoTooltip, false)
					}
				>
					<Form.Label className={!disabled && !readOnly && touched && invalid ? `is-invalid` : ""}>
						{label}
						<span className="text-nowrap">
							&nbsp;
							<FontAwesomeIcon icon={fasCircleQuestion} />
						</span>
					</Form.Label>
				</OverlayTrigger>
			);
		}

		return (
			<Form.Label className={!disabled && !readOnly && touched && invalid ? `is-invalid` : ""}>
				{label}
			</Form.Label>
		);
	}

	protected abstract renderField(fieldProps: FieldRenderProps<FieldType>): ReactNode;

	protected renderValidation(fieldProps: FieldRenderProps<FieldType>): ReactNode {
		const { disabled, readOnly } = this.props;
		const {
			meta: { invalid, error, touched },
		} = fieldProps;

		if (!disabled && !readOnly && error && invalid && touched) {
			if (VALIDATION_TOOLTIP) {
				return <div className="animated fadeInDown faster invalid-tooltip">{error}</div>;
			}

			return (
				<Form.Control.Feedback className="animated fadeInDown faster" type="invalid">
					{error}
				</Form.Control.Feedback>
			);
		}

		return null;
	}

	protected renderInfo(info: ReactNode): ReactNode {
		if (info) {
			return <Form.Text className="text-muted">{info}</Form.Text>;
		}

		return null;
	}

	protected getGroupClassName(fieldProps: FieldRenderProps<FieldType>): string {
		const { disabled, required, className, readOnly, label } = this.props;
		const {
			meta: { invalid, valid, touched },
		} = fieldProps;

		return compact([
			className,
			!label && "without-label",
			required && "required",
			disabled && "disabled",
			!disabled && !readOnly && touched && invalid && "has-danger",
			!disabled && !readOnly && touched && valid && "has-success",
		]).join(" ");
	}

	@autobind
	protected parse(value: any): FieldType {
		return value as unknown as FieldType;
	}

	@autobind
	protected format(value: any): any {
		return value as unknown as string;
	}

	@autobind
	protected handleChange(value: FieldType, fieldProps: FieldRenderProps<FieldType>): void {
		const { onChange: onPropsChange } = this.props;
		const {
			input: { onChange },
		} = fieldProps;

		onChange(value);

		if (onPropsChange) {
			onPropsChange(value);
		}
	}

	@autobind
	protected handleFocus(event: React.FocusEvent<HTMLElement>, fieldProps: FieldRenderProps<FieldType>): void {
		const { onFocus: onPropsFocus } = this.props;
		const {
			input: { onFocus },
		} = fieldProps;

		onFocus(event);

		if (onPropsFocus) {
			onPropsFocus(event);
		}
	}

	@autobind
	protected handleBlur(event: React.FocusEvent<HTMLElement>, fieldProps: FieldRenderProps<FieldType>): void {
		const { onBlur: onPropsBlur } = this.props;
		const {
			input: { onBlur, value },
		} = fieldProps;

		onBlur(event);

		if (onPropsBlur) {
			onPropsBlur(value, event);
		}
	}

	@autobind
	protected handleValidate(value: FieldType, allValues: Record<string, any>, meta?: FieldState<FieldType>): string {
		const { validate } = this.props;

		if (!this.isDisabled() && validate) {
			return validate(value, allValues, meta);
		}

		return undefined;
	}

	protected isValidationShow(fieldProps: FieldRenderProps<FieldType>): boolean {
		const {
			meta: { invalid, error, touched },
		} = fieldProps;

		if (!error) {
			return false;
		}

		return !this.isDisabled() && invalid && touched ? undefined : false;
	}

	protected isValidationDefaultShow(fieldProps: FieldRenderProps<FieldType>): boolean {
		const {
			meta: { active },
		} = fieldProps;

		return active;
	}
}
